import React, { useState, useMemo } from "react";

/**
 * Group sessions into ChatGPT / Claude style relative date categories
 * with dateKey (YYYY-MM-DD) attached for date-based bulk deletion.
 */
function groupSessions(sessions) {
  const groups = [];
  const now = new Date();
  const todayKey = now.toISOString().slice(0, 10);

  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(now.getDate() - 1);
  const yesterdayKey = yesterdayDate.toISOString().slice(0, 10);

  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(now.getDate() - 7);

  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);

  // Group buckets
  const today = [];
  const yesterday = [];
  const last7Days = [];
  const last30Days = [];
  const olderMap = new Map();

  for (const s of sessions) {
    const rawDate = s.modifiedDate || s.createdDate || s.updatedAt || s.dateKey;
    const sDate = rawDate ? new Date(rawDate) : new Date();
    const sDateKey = s.dateKey || (rawDate ? new Date(rawDate).toISOString().slice(0, 10) : todayKey);

    if (sDateKey === todayKey) {
      today.push(s);
    } else if (sDateKey === yesterdayKey) {
      yesterday.push(s);
    } else if (sDate >= sevenDaysAgo) {
      last7Days.push(s);
    } else if (sDate >= thirtyDaysAgo) {
      last30Days.push(s);
    } else {
      const monthYear = sDate.toLocaleDateString(undefined, { month: "short", year: "numeric" });
      if (!olderMap.has(monthYear)) {
        olderMap.set(monthYear, []);
      }
      olderMap.get(monthYear).push(s);
    }
  }

  if (today.length > 0) {
    groups.push({ label: "Today", dateKey: todayKey, items: today });
  }
  if (yesterday.length > 0) {
    groups.push({ label: "Yesterday", dateKey: yesterdayKey, items: yesterday });
  }
  if (last7Days.length > 0) {
    groups.push({ label: "Previous 7 Days", dateKey: null, items: last7Days });
  }
  if (last30Days.length > 0) {
    groups.push({ label: "Previous 30 Days", dateKey: null, items: last30Days });
  }

  for (const [monthYear, items] of olderMap.entries()) {
    groups.push({ label: monthYear, dateKey: null, items });
  }

  return groups;
}

export default function ChatHistorySidebar({
  sessions = [],
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  onDeleteDate,
  onClearAll,
  onClose,
  isOverlay = false,
  isLoading = false,
}) {
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [dateToDelete, setDateToDelete] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const grouped = useMemo(() => groupSessions(sessions), [sessions]);

  const handleDeleteSessionConfirm = (e, sessionId) => {
    e.stopPropagation();
    onDeleteSession?.(sessionId);
    setSessionToDelete(null);
  };

  const handleDeleteDateConfirm = (e, dateKey, label) => {
    e.stopPropagation();
    onDeleteDate?.(dateKey, label);
    setDateToDelete(null);
  };

  return (
    <aside
      className={`flex flex-col h-full bg-slate-50 dark:bg-slate-900/95 border-r border-slate-200/90 dark:border-slate-800 transition-all select-none ${
        isOverlay ? "w-72 shadow-2xl" : "w-64"
      }`}
    >
      {/* Sidebar Header & New Chat Button */}
      <div className="p-3 border-b border-slate-200/80 dark:border-slate-800 flex flex-col gap-2 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Chat History</span>
          </div>

          {isOverlay && (
            <button
              type="button"
              onClick={onClose}
              title="Close History"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 cursor-pointer"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* + New Chat Button */}
        <button
          type="button"
          onClick={() => {
            onNewChat?.();
            if (isOverlay) onClose?.();
          }}
          className="flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl border border-slate-200/90 dark:border-slate-700 bg-white dark:bg-slate-800/90 text-xs font-semibold text-slate-800 dark:text-slate-100 shadow-xs hover:border-primary-500 hover:bg-primary-50/50 dark:hover:border-primary-500/50 dark:hover:bg-primary-950/30 transition active:scale-[0.98] cursor-pointer group"
        >
          <div className="flex items-center gap-2.5">
            <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-500 text-white shadow-xs group-hover:scale-105 transition">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span>New Chat</span>
          </div>
          <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">Ctrl+N</span>
        </button>
      </div>

      {/* Scrollable Conversation History List */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-4 slim-scrollbar">
        {isLoading && sessions.length === 0 ? (
          <div className="p-4 space-y-2.5">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-9 rounded-xl bg-slate-200/70 dark:bg-slate-800 animate-pulse" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-3 py-10 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 mb-2">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300">No chat history yet</p>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              Your conversations will be saved here automatically.
            </p>
          </div>
        ) : (
          grouped.map((group, gIdx) => (
            <div key={gIdx} className="space-y-1">
              {/* Date Header with optional "Delete Day" icon */}
              <div className="flex items-center justify-between px-2 pt-1 pb-1">
                <span className="text-[11px] font-bold tracking-wider text-slate-400 dark:text-slate-500 uppercase">
                  {group.label}
                </span>

                {group.dateKey && (
                  <div className="relative">
                    {dateToDelete === group.dateKey ? (
                      <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-900 rounded-lg p-0.5 shadow-xs">
                        <span className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold px-1">
                          Delete day?
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteDateConfirm(e, group.dateKey, group.label)}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-rose-500 hover:bg-rose-600 text-white font-medium cursor-pointer"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDateToDelete(null);
                          }}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateToDelete(group.dateKey);
                        }}
                        title={`Delete all chats from ${group.label}`}
                        className="opacity-40 hover:opacity-100 p-1 text-slate-400 hover:text-rose-500 transition cursor-pointer"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Chat Session Items */}
              {group.items.map((session) => {
                const isActive = session.sessionId === activeSessionId;
                const isConfirmingDelete = sessionToDelete === session.sessionId;

                return (
                  <div
                    key={session.sessionId}
                    onClick={() => {
                      if (!isConfirmingDelete) {
                        onSelectSession?.(session);
                        if (isOverlay) onClose?.();
                      }
                    }}
                    className={`group relative flex items-center justify-between px-3 py-2 rounded-xl text-xs transition cursor-pointer ${
                      isActive
                        ? "bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 font-semibold border border-primary-200/80 dark:border-primary-800/60 shadow-2xs"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100/90 dark:hover:bg-slate-800/70 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden flex-1 min-w-0">
                      <svg
                        className={`h-4 w-4 shrink-0 transition ${
                          isActive
                            ? "text-primary-600 dark:text-primary-400"
                            : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.75}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                      <span className="truncate" title={session.title}>
                        {session.title || "Untitled Conversation"}
                      </span>
                    </div>

                    {/* Single Chat Delete Action */}
                    <div className="shrink-0 ml-1.5 flex items-center">
                      {isConfirmingDelete ? (
                        <div
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={(e) => handleDeleteSessionConfirm(e, session.sessionId)}
                            title="Confirm delete"
                            className="p-1 rounded bg-rose-500 text-white hover:bg-rose-600 cursor-pointer"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionToDelete(null);
                            }}
                            title="Cancel"
                            className="p-1 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSessionToDelete(session.sessionId);
                          }}
                          title="Delete conversation"
                          className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-500 rounded-md transition cursor-pointer"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Sidebar Footer: Clear All History */}
      {sessions.length > 0 && (
        <div className="p-2 border-t border-slate-200/80 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 shrink-0">
          {confirmClearAll ? (
            <div className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 space-y-1.5">
              <p className="text-[11px] font-semibold text-rose-800 dark:text-rose-200 text-center">
                Delete all conversation history?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onClearAll?.();
                    setConfirmClearAll(false);
                  }}
                  className="flex-1 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold cursor-pointer"
                >
                  Yes, Clear All
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClearAll(false)}
                  className="flex-1 py-1 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[10px] font-medium cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmClearAll(true)}
              className="flex items-center justify-center gap-2 w-full py-1.5 px-3 rounded-lg text-[11px] text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50/50 dark:hover:bg-rose-950/20 transition cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Clear all chat history</span>
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

